import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { RotateCcw } from "lucide-react";
import { useState } from "react";
import { DOC_PERMISSION_RANK, type WriteRejection } from "@estelle/domain";

import { useDataSource } from "@/adapters/data-source";
import { EmptyState, LoadFailed } from "@/components/shell/EmptyState";
import { TableSkeleton } from "@/components/shell/LoadingState";
import { Button } from "@/components/ui/button";
import { formatAbsolute, relativeTime } from "@/modules/docs/versions";
import { RejectionNotice } from "@/modules/docs/screens/RejectionNotice";
import { FolderTree } from "@/modules/docs/screens/FolderTree";
import { useDocsPeople } from "@/modules/docs/useDocsPeople";
import { useDocsTree } from "@/modules/docs/useDocsTree";
import { useSectionSearch } from "@/routes/search";

/**
 * One folder's soft-deleted pages, and the Restore action that undoes it
 * (FR-DOC-13, #213).
 *
 * Visible only to whoever holds Manage on the folder in question — the same
 * shape `Permissions` already uses for `?record=<folderId>` links from a
 * folder's "⋯" menu, and enforced the same way: the server answers an empty
 * list to anyone else (`GET .../deleted-pages`), this screen's own folder
 * list is filtered to Manage as a courtesy on top of that.
 */
export function Deleted() {
  const data = useDataSource();
  const queryClient = useQueryClient();
  const treeQuery = useDocsTree();
  const people = useDocsPeople();
  const search = useSectionSearch();

  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [rejection, setRejection] = useState<WriteRejection | null>(null);

  const folders = treeQuery.data?.folders ?? [];
  const manageableFolders = folders.filter(
    (folder) =>
      DOC_PERMISSION_RANK[folder.effectiveLevel] >= DOC_PERMISSION_RANK.manage,
  );

  const effectiveFolderId =
    selectedFolderId &&
    manageableFolders.some((folder) => folder.id === selectedFolderId)
      ? selectedFolderId
      : (manageableFolders.find((folder) => folder.id === search.record)?.id ??
        manageableFolders[0]?.id ??
        null);

  const deletedQuery = useQuery({
    queryKey: ["docs", "deleted", effectiveFolderId],
    queryFn: () => data.docs.listDeletedPages(effectiveFolderId ?? ""),
    enabled: effectiveFolderId !== null,
  });

  const restoreMutation = useMutation({
    mutationFn: (pageId: string) => data.docs.restorePage(pageId),
    onSuccess: (result) => {
      if (result.ok) {
        setRejection(null);
        void queryClient.invalidateQueries({
          queryKey: ["docs", "deleted", effectiveFolderId],
        });
        // The restored page reappears in the tree and in `listPages`.
        void queryClient.invalidateQueries({ queryKey: ["docs", "tree"] });
      } else {
        setRejection(result.rejection);
      }
    },
  });

  if (treeQuery.isPending || people.isPending)
    return <TableSkeleton rows={6} />;

  if (treeQuery.isError) {
    return (
      <LoadFailed
        message={treeQuery.error.message}
        onRetry={() => {
          void treeQuery.refetch();
        }}
      />
    );
  }

  if (manageableFolders.length === 0) {
    return (
      <EmptyState
        title="No folders you manage"
        description="Deleted pages show up here for a folder once you hold Manage on it."
      />
    );
  }

  const selectedFolder = manageableFolders.find(
    (folder) => folder.id === effectiveFolderId,
  );

  return (
    <div className="flex h-full min-h-0 flex-col gap-4 md:flex-row">
      <div className="max-h-48 w-full shrink-0 overflow-auto border-b pb-2 md:max-h-none md:w-64 md:border-r md:border-b-0 md:pr-2">
        <FolderTree
          folders={manageableFolders}
          selectedFolderId={effectiveFolderId}
          onSelectFolder={setSelectedFolderId}
        />
      </div>

      <div className="min-h-0 flex-1 space-y-4 overflow-auto pb-4">
        <div>
          <h2 className="text-lg font-semibold">
            {selectedFolder?.name ?? "Deleted"}
          </h2>
          <p className="text-muted-foreground text-xs">
            {selectedFolder?.path}
          </p>
        </div>

        {rejection ? <RejectionNotice rejection={rejection} /> : null}

        {deletedQuery.isPending ? (
          <TableSkeleton rows={4} />
        ) : deletedQuery.isError ? (
          <LoadFailed
            message={deletedQuery.error.message}
            onRetry={() => {
              void deletedQuery.refetch();
            }}
          />
        ) : deletedQuery.data.length === 0 ? (
          <EmptyState
            title="Nothing deleted here"
            description="Pages deleted from this folder will show up here, recoverable at any time."
          />
        ) : (
          <ul className="divide-border divide-y rounded-md border">
            {deletedQuery.data.map((page) => (
              <li
                key={page.id}
                className="flex flex-wrap items-center justify-between gap-3 px-4 py-3"
              >
                <div className="min-w-0 space-y-0.5">
                  <p className="truncate font-medium">{page.title}</p>
                  <p className="text-muted-foreground text-xs">
                    Deleted by{" "}
                    {page.deletedByUserId
                      ? people.principalName("user", page.deletedByUserId)
                      : "someone"}
                    {page.deletedAt ? (
                      <span title={formatAbsolute(page.deletedAt)}>
                        {" "}
                        · {relativeTime(page.deletedAt)}
                      </span>
                    ) : null}
                  </p>
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={restoreMutation.isPending}
                  onClick={() => {
                    restoreMutation.mutate(page.id);
                  }}
                >
                  <RotateCcw /> Restore
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
